/**
 * 점검 탭 컴포넌트 (점검항목 + 점검사진 통합)
 *
 * 기존 ChecklistTab과 InspectionPhotoTab을 하나로 통합
 * - 체크리스트 항목을 카테고리별로 그룹화하여 표시
 * - O/X 선택 시 즉시 사진 업로드 영역 표시
 * - 부적합(X) 선택 시 원인/조치 입력 필드 표시
 */

import { useMemo, useRef, useCallback, useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { ImagePlus, Loader2 } from "lucide-react";
import type { CheckListItem, CheckResultValueNullable } from "@/types";
import { CHECK_RESULTS } from "@/types";
import { uploadCheckItemImage } from "@/lib/upload";
import { useToast } from "@/contexts/ToastContext";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { InAppCamera } from "@/components/camera/InAppCamera";
import { base64ToFile, generatePhotoFileName } from "@/lib/camera-utils";
import { createLogger } from "@ict/shared";
import { PhotoCaptureSlot } from "@/components/inspection/shared/PhotoCaptureSlot";
import { ImagePreviewModal, getImageSrc } from "@/components/inspection/shared/ImagePreviewModal";

const logger = createLogger({ module: "ChecklistTab" });

// ============================================================================
// Types & Interfaces
// ============================================================================

interface ChecklistTabProps {
  items: CheckListItem[];
  onChange: (item: CheckListItem) => void;
  onPhotoChange?: (item: CheckListItem) => void;
  isLoading?: boolean;
  /** @deprecated isSaving 대신 savingItemId 사용 */
  isSaving?: boolean;
  /** 현재 저장 중인 항목 ID (개별 로딩 표시용) */
  savingItemId?: string | null;
  // 이미지 업로드용 파라미터
  projectId?: string;
  docKey?: string;
  machineId?: string;
}

interface GroupedCheckItem {
  group: string;
  items: CheckListItem[];
}

type ImageFieldName = "image" | "imageO" | "imageX";

// ============================================================================
// Constants
// ============================================================================

// 그룹별 색상 매핑 (Industrial Minimal)
const GROUP_COLORS: Record<string, { accent: string; text: string }> = {
  외관: { accent: "bg-sky-500", text: "text-sky-700" },
  기능: { accent: "bg-violet-500", text: "text-violet-700" },
  안전: { accent: "bg-orange-500", text: "text-orange-700" },
  기타: { accent: "bg-slate-400", text: "text-slate-600" },
};

// ============================================================================
// Utility Functions
// ============================================================================

function getGroupStyle(groupName: string) {
  return GROUP_COLORS[groupName] || GROUP_COLORS["기타"];
}

/**
 * 점검항목을 그룹별로 분류
 * - 모든 항목에 그룹이 없으면 (성능점검) → 플랫하게 표시 (그룹 헤더 없음)
 * - 그룹이 있는 항목이 하나라도 있으면 (유지관리) → 그룹별로 분류
 */
function groupCheckItems(items: CheckListItem[]): { groups: GroupedCheckItem[]; isFlat: boolean } {
  const groupMap = new Map<string, CheckListItem[]>();
  const groupOrder = ["외관", "기능", "안전", ""];

  // 실제 그룹이 있는 항목이 있는지 확인
  const hasRealGroups = items.some(
    (item) => item.category && item.category.trim() !== "" && item.category !== "기타"
  );

  for (const item of items) {
    const group = item.category || "";
    if (!groupMap.has(group)) {
      groupMap.set(group, []);
    }
    groupMap.get(group)!.push(item);
  }

  // 그룹이 없으면 플랫하게 반환
  if (!hasRealGroups) {
    return {
      groups: [{ group: "", items }],
      isFlat: true,
    };
  }

  // 그룹 순서대로 정렬
  const result: GroupedCheckItem[] = [];
  for (const group of groupOrder) {
    if (groupMap.has(group) && groupMap.get(group)!.length > 0) {
      result.push({
        group: group || "기타",
        items: groupMap.get(group)!,
      });
    }
  }

  // 순서에 없는 그룹 추가
  for (const [group, groupItems] of groupMap) {
    if (!groupOrder.includes(group) && groupItems.length > 0) {
      result.push({ group, items: groupItems });
    }
  }

  return { groups: result, isFlat: false };
}

// ============================================================================
// Custom Hooks
// ============================================================================

/** 이미지 업로드 로직을 관리하는 커스텀 훅 */
function useItemImageUpload(
  item: CheckListItem,
  onChange: (item: CheckListItem) => void,
  options: { projectId: string; docKey: string; machineId: string; itemId: string }
) {
  const { showToast } = useToast();
  const { projectId, docKey, machineId, itemId } = options;
  const [uploading, setUploading] = useState<Record<ImageFieldName, boolean>>({
    image: false,
    imageO: false,
    imageX: false,
  });
  // 언마운트 후 state 업데이트 방지
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleImageUpload = useCallback(
    async (fieldName: ImageFieldName, file: File) => {
      if (!itemId) return;

      setUploading((prev) => ({ ...prev, [fieldName]: true }));

      try {
        const result = await uploadCheckItemImage(file, {
          projectId,
          docKey,
          machineId,
          checkId: itemId,
          fieldName,
        });

        // 언마운트 체크 후 state 업데이트
        if (!isMountedRef.current) return;

        if (result.success && result.url) {
          onChange({ ...item, [fieldName]: result.url });
        } else {
          logger.error("이미지 업로드 실패:", result.error);
          showToast(result.error || "이미지 업로드에 실패했습니다.", "error");
        }
      } catch (error) {
        logger.error("이미지 업로드 오류:", error);
      } finally {
        // 언마운트 체크 후 state 업데이트
        if (isMountedRef.current) {
          setUploading((prev) => ({ ...prev, [fieldName]: false }));
        }
      }
    },
    [item, onChange, projectId, docKey, machineId, itemId, showToast]
  );

  const handleImageDelete = useCallback(
    (fieldName: ImageFieldName) => {
      onChange({ ...item, [fieldName]: undefined });
    },
    [item, onChange]
  );

  return { uploading, handleImageUpload, handleImageDelete };
}

/** 이미지 미리보기/삭제 확인 로직을 관리하는 커스텀 훅 */
function useImagePreviewAndDelete(
  itemId: string,
  handleImageDelete: (fieldName: ImageFieldName) => void,
  onOpenCamera?: (itemId: string, fieldName: ImageFieldName) => void
) {
  // 미리보기 모달 상태
  const [previewState, setPreviewState] = useState<{
    isOpen: boolean;
    imageUrl: string;
    fieldName: ImageFieldName;
    label: string;
  } | null>(null);

  // 삭제 확인 다이얼로그 상태
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    fieldName: ImageFieldName;
    label: string;
  } | null>(null);

  const handlePreview = useCallback(
    (fieldName: ImageFieldName, imageUrl: string, label: string) => {
      setPreviewState({ isOpen: true, imageUrl, fieldName, label });
    },
    []
  );

  const handleClosePreview = useCallback(() => {
    setPreviewState(null);
  }, []);

  const handleRetake = useCallback(() => {
    if (previewState) {
      const fieldName = previewState.fieldName;
      setPreviewState(null);
      if (onOpenCamera) {
        onOpenCamera(itemId, fieldName);
      }
    }
  }, [previewState, itemId, onOpenCamera]);

  const handleDeleteClick = useCallback((fieldName: ImageFieldName, label: string) => {
    setDeleteConfirm({ isOpen: true, fieldName, label });
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (deleteConfirm) {
      handleImageDelete(deleteConfirm.fieldName);
      setDeleteConfirm(null);
    }
  }, [deleteConfirm, handleImageDelete]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirm(null);
  }, []);

  return {
    previewState,
    deleteConfirm,
    handlePreview,
    handleClosePreview,
    handleRetake,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
  };
}

// ============================================================================
// Sub Components
// ============================================================================

/** 토스 스타일 3상태 버튼 */
function StateButtons({
  value,
  onChange,
  disabled,
}: {
  value: CheckResultValueNullable;
  onChange: (value: CheckResultValueNullable) => void;
  disabled?: boolean;
}) {
  const handleClick = (newValue: CheckResultValueNullable) => {
    // 같은 버튼 다시 눌러도 해제 안됨
    if (value === newValue) return;
    onChange(newValue);
  };

  return (
    <div className="flex items-center gap-2 shrink-0">
      <button
        type="button"
        onClick={() => handleClick(CHECK_RESULTS.OK)}
        disabled={disabled}
        aria-label="양호"
        className={`w-11 h-11 text-sm font-semibold rounded-full transition-colors ${
          value === CHECK_RESULTS.OK
            ? "bg-[#3182F6] text-white"
            : "bg-gray-100 text-gray-400 active:bg-gray-200"
        } disabled:opacity-40`}
      >
        O
      </button>
      <button
        type="button"
        onClick={() => handleClick(CHECK_RESULTS.FAIL)}
        disabled={disabled}
        aria-label="부적합"
        className={`w-11 h-11 text-sm font-semibold rounded-full transition-colors ${
          value === CHECK_RESULTS.FAIL
            ? "bg-[#F04452] text-white"
            : "bg-gray-100 text-gray-400 active:bg-gray-200"
        } disabled:opacity-40`}
      >
        X
      </button>
      <button
        type="button"
        onClick={() => handleClick(CHECK_RESULTS.NA)}
        disabled={disabled}
        aria-label="해당없음"
        className={`w-11 h-11 text-sm font-semibold rounded-full transition-colors ${
          value === CHECK_RESULTS.NA
            ? "bg-gray-500 text-white"
            : "bg-gray-100 text-gray-400 active:bg-gray-200"
        } disabled:opacity-40`}
      >
        /
      </button>
    </div>
  );
}

/** 토스 스타일 이미지 업로드 슬롯 */
function ImageUploadSlot({
  fieldName,
  itemId,
  imageUrl,
  label,
  uploading,
  onUpload,
  onDelete,
  onPreview,
  onCameraOpen,
  disabled,
}: {
  fieldName: ImageFieldName;
  itemId: string;
  imageUrl?: string;
  label: string;
  uploading?: boolean;
  onUpload: (file: File) => void;
  onDelete: () => void;
  onPreview: (imageUrl: string) => void;
  /** 네이티브 인앱 카메라 열기 콜백 (네이티브 환경에서만 전달) */
  onCameraOpen?: () => void;
  disabled?: boolean;
}) {
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // 갤러리 선택 클릭
  const handleGalleryClick = useCallback(() => {
    galleryInputRef.current?.click();
  }, []);

  // 기존 이미지 클릭 시 미리보기 모달 열기
  const handleImageClick = useCallback(() => {
    if (imageUrl) {
      onPreview(imageUrl);
    }
  }, [imageUrl, onPreview]);

  const handleGalleryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        onUpload(file);
      }
      if (galleryInputRef.current) {
        galleryInputRef.current.value = "";
      }
    },
    [onUpload]
  );

  const testIds = useMemo(
    () => ({
      slot: `upload-slot-${fieldName}-${itemId}`,
      preview: `image-preview-${fieldName}-${itemId}`,
      delete: `delete-image-${fieldName}-${itemId}`,
      input: `file-input-${fieldName}-${itemId}`,
      galleryInput: `gallery-input-${fieldName}-${itemId}`,
      loading: `upload-loading-${fieldName}-${itemId}`,
    }),
    [fieldName, itemId]
  );

  // 이미지 URL 변환 (로컬/서버 지원)
  const displayImageUrl = useMemo(() => getImageSrc(imageUrl), [imageUrl]);

  // 이미지가 있는 경우
  if (imageUrl) {
    return (
      <div data-testid={testIds.slot} className="flex flex-col gap-2">
        {/* 이미지 영역 */}
        <div className="relative">
          <img
            data-testid={testIds.preview}
            src={displayImageUrl}
            alt={label}
            className="w-full aspect-[4/3] object-cover rounded-xl cursor-pointer"
            onClick={handleImageClick}
          />
          {/* 개별 로딩 오버레이 */}
          {uploading && (
            <div
              data-testid={testIds.loading}
              className="absolute inset-0 bg-black/30 flex items-center justify-center rounded-xl"
            >
              <Loader2 className="w-6 h-6 animate-spin text-white" />
            </div>
          )}
          <button
            type="button"
            data-testid={testIds.delete}
            onClick={onDelete}
            disabled={disabled || uploading}
            className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs active:bg-red-600 disabled:opacity-50 shadow-sm"
            aria-label="이미지 삭제"
          >
            ×
          </button>
        </div>

        {/* 갤러리 버튼 (사진 교체용) - 세련된 그라데이션 스타일 */}
        <button
          type="button"
          onClick={handleGalleryClick}
          disabled={disabled || uploading}
          className="group relative w-full py-2.5 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200/80 flex items-center justify-center gap-1.5 active:scale-[0.98] transition-all duration-150 disabled:opacity-50 overflow-hidden"
          aria-label={`${label} 갤러리에서 교체`}
        >
          {/* 호버/액티브 시 배경 효과 */}
          <span className="absolute inset-0 bg-gradient-to-r from-blue-100/0 to-blue-100/50 opacity-0 group-active:opacity-100 transition-opacity" />
          <ImagePlus className="w-4 h-4 text-blue-500 relative z-10" />
          <span className="text-xs font-medium text-slate-600 relative z-10">갤러리에서 교체</span>
        </button>

        {/* Hidden file input (갤러리 교체용) */}
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          data-testid={testIds.galleryInput}
          onChange={handleGalleryChange}
          className="hidden"
        />
      </div>
    );
  }

  // 빈 슬롯 - PhotoCaptureSlot 공통 컴포넌트 사용
  return (
    <div data-testid={testIds.slot}>
      <PhotoCaptureSlot
        label={label}
        onFileSelect={onUpload}
        onCameraOpen={onCameraOpen}
        uploading={uploading}
        disabled={disabled}
      />
    </div>
  );
}

/** 사진 업로드 영역 (O 선택 시) */
function PhotoSectionOk({
  item,
  onChange,
  disabled,
  projectId,
  docKey,
  machineId,
  onCameraOpen,
}: {
  item: CheckListItem;
  onChange: (item: CheckListItem) => void;
  disabled?: boolean;
  projectId: string;
  docKey: string;
  machineId: string;
  /** 인앱 카메라 열기 (fieldName 전달) */
  onCameraOpen?: (itemId: string, fieldName: ImageFieldName) => void;
}) {
  const itemId = item._id || "";
  const { uploading, handleImageUpload, handleImageDelete } = useItemImageUpload(item, onChange, {
    projectId,
    docKey,
    machineId,
    itemId,
  });

  // 커스텀 훅으로 미리보기/삭제 로직 추출
  const {
    previewState,
    deleteConfirm,
    handlePreview,
    handleClosePreview,
    handleRetake,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
  } = useImagePreviewAndDelete(
    itemId,
    handleImageDelete,
    onCameraOpen ? (id, field) => onCameraOpen(id, field) : undefined
  );

  // 네이티브 환경에서 인앱 카메라 열기 핸들러
  const handleCameraOpen = useCallback(
    (fieldName: ImageFieldName) => {
      if (onCameraOpen) {
        onCameraOpen(itemId, fieldName);
      }
    },
    [onCameraOpen, itemId]
  );

  return (
    <div className="px-4 pb-4 pt-2">
      <div className="grid grid-cols-2 gap-3">
        <ImageUploadSlot
          fieldName="image"
          itemId={itemId}
          imageUrl={item.image}
          label="점검사진"
          uploading={uploading.image}
          onUpload={(file) => handleImageUpload("image", file)}
          onDelete={() => handleDeleteClick("image", "점검사진")}
          onPreview={(url) => handlePreview("image", url, "점검사진")}
          onCameraOpen={onCameraOpen ? () => handleCameraOpen("image") : undefined}
          disabled={disabled}
        />
        <ImageUploadSlot
          fieldName="imageO"
          itemId={itemId}
          imageUrl={item.imageO}
          label="적합사진"
          uploading={uploading.imageO}
          onUpload={(file) => handleImageUpload("imageO", file)}
          onDelete={() => handleDeleteClick("imageO", "적합사진")}
          onPreview={(url) => handlePreview("imageO", url, "적합사진")}
          onCameraOpen={onCameraOpen ? () => handleCameraOpen("imageO") : undefined}
          disabled={disabled}
        />
      </div>

      {/* 미리보기 모달 */}
      {previewState && (
        <ImagePreviewModal
          isOpen={previewState.isOpen}
          imageUrl={previewState.imageUrl}
          label={previewState.label}
          onClose={handleClosePreview}
          onRetake={handleRetake}
        />
      )}

      {/* 삭제 확인 다이얼로그 */}
      <ConfirmDialog
        isOpen={deleteConfirm?.isOpen ?? false}
        title="사진 삭제"
        message={`${deleteConfirm?.label || "사진"}을 삭제하시겠습니까?`}
        confirmText="삭제"
        cancelText="취소"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
}

/** 사진 업로드 영역 + 원인/조치 입력 (X 선택 시) */
function PhotoSectionFail({
  item,
  onChange,
  disabled,
  projectId,
  docKey,
  machineId,
  onCameraOpen,
}: {
  item: CheckListItem;
  onChange: (item: CheckListItem) => void;
  disabled?: boolean;
  projectId: string;
  docKey: string;
  machineId: string;
  /** 인앱 카메라 열기 (fieldName 전달) */
  onCameraOpen?: (itemId: string, fieldName: ImageFieldName) => void;
}) {
  const itemId = item._id || "";
  const { uploading, handleImageUpload, handleImageDelete } = useItemImageUpload(item, onChange, {
    projectId,
    docKey,
    machineId,
    itemId,
  });

  // 커스텀 훅으로 미리보기/삭제 로직 추출
  const {
    previewState,
    deleteConfirm,
    handlePreview,
    handleClosePreview,
    handleRetake,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
  } = useImagePreviewAndDelete(
    itemId,
    handleImageDelete,
    onCameraOpen ? (id, field) => onCameraOpen(id, field) : undefined
  );

  // 네이티브 환경에서 인앱 카메라 열기 핸들러
  const handleCameraOpen = useCallback(
    (fieldName: ImageFieldName) => {
      if (onCameraOpen) {
        onCameraOpen(itemId, fieldName);
      }
    },
    [onCameraOpen, itemId]
  );

  // 로컬 상태로 입력값 관리 (매 글자 저장 방지)
  const [localReason, setLocalReason] = useState(item.underReason || "");
  const [localSolution, setLocalSolution] = useState(item.underSolution || "");

  // 설비 변경 시에만 로컬 상태 동기화 (입력 중 서버 업데이트로 덮어씌워지는 것 방지)
  useEffect(() => {
    setLocalReason(item.underReason || "");
    setLocalSolution(item.underSolution || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item._id]);

  // 로컬 상태만 업데이트 (서버 저장 안함)
  const handleReasonChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalReason(e.target.value);
  }, []);

  const handleSolutionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalSolution(e.target.value);
  }, []);

  // blur 시에만 서버 저장 (변경된 경우에만)
  const handleReasonBlur = useCallback(() => {
    if (localReason !== (item.underReason || "")) {
      onChange({ ...item, underReason: localReason });
    }
  }, [localReason, item, onChange]);

  const handleSolutionBlur = useCallback(() => {
    if (localSolution !== (item.underSolution || "")) {
      onChange({ ...item, underSolution: localSolution });
    }
  }, [localSolution, item, onChange]);

  return (
    <div className="px-4 pb-4 pt-2 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <ImageUploadSlot
          fieldName="image"
          itemId={itemId}
          imageUrl={item.image}
          label="점검사진"
          uploading={uploading.image}
          onUpload={(file) => handleImageUpload("image", file)}
          onDelete={() => handleDeleteClick("image", "점검사진")}
          onPreview={(url) => handlePreview("image", url, "점검사진")}
          onCameraOpen={onCameraOpen ? () => handleCameraOpen("image") : undefined}
          disabled={disabled}
        />
        <ImageUploadSlot
          fieldName="imageX"
          itemId={itemId}
          imageUrl={item.imageX}
          label="부적합사진"
          uploading={uploading.imageX}
          onUpload={(file) => handleImageUpload("imageX", file)}
          onDelete={() => handleDeleteClick("imageX", "부적합사진")}
          onPreview={(url) => handlePreview("imageX", url, "부적합사진")}
          onCameraOpen={onCameraOpen ? () => handleCameraOpen("imageX") : undefined}
          disabled={disabled}
        />
      </div>

      <div className="space-y-2">
        <textarea
          rows={2}
          data-testid={`under-reason-input-${itemId}`}
          placeholder="부적합 원인"
          value={localReason}
          onChange={handleReasonChange}
          onBlur={handleReasonBlur}
          disabled={disabled}
          className="w-full px-4 py-3 text-sm bg-gray-100 rounded-xl resize-none focus:outline-none focus:bg-gray-50 focus:ring-2 focus:ring-[#3182F6] disabled:opacity-50 placeholder:text-gray-400"
        />
        <textarea
          rows={2}
          data-testid={`under-solution-input-${itemId}`}
          placeholder="조치 내용"
          value={localSolution}
          onChange={handleSolutionChange}
          onBlur={handleSolutionBlur}
          disabled={disabled}
          className="w-full px-4 py-3 text-sm bg-gray-100 rounded-xl resize-none focus:outline-none focus:bg-gray-50 focus:ring-2 focus:ring-[#3182F6] disabled:opacity-50 placeholder:text-gray-400"
        />
      </div>

      {/* 미리보기 모달 */}
      {previewState && (
        <ImagePreviewModal
          isOpen={previewState.isOpen}
          imageUrl={previewState.imageUrl}
          label={previewState.label}
          onClose={handleClosePreview}
          onRetake={handleRetake}
        />
      )}

      {/* 삭제 확인 다이얼로그 */}
      <ConfirmDialog
        isOpen={deleteConfirm?.isOpen ?? false}
        title="사진 삭제"
        message={`${deleteConfirm?.label || "사진"}을 삭제하시겠습니까?`}
        confirmText="삭제"
        cancelText="취소"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
}

/** 체크리스트 아이템 - 왼쪽 컬러 바로 상태 표시 */
function ChecklistItem({
  item,
  index,
  isLast,
  onResultChange,
  onPhotoChange,
  disabled,
  isSaving,
  projectId,
  docKey,
  machineId,
  onCameraOpen,
}: {
  item: CheckListItem;
  index: number;
  isLast: boolean;
  onResultChange: (result: CheckResultValueNullable) => void;
  onPhotoChange?: (item: CheckListItem) => void;
  disabled?: boolean;
  /** 현재 이 항목이 저장 중인지 여부 */
  isSaving?: boolean;
  projectId?: string;
  docKey?: string;
  machineId?: string;
  /** 인앱 카메라 열기 콜백 */
  onCameraOpen?: (itemId: string, fieldName: ImageFieldName) => void;
}) {
  const showPhotoSection =
    projectId &&
    docKey &&
    machineId &&
    onPhotoChange &&
    (item.result === CHECK_RESULTS.OK || item.result === CHECK_RESULTS.FAIL);

  // 결과 상태에 따른 왼쪽 바 색상
  const getStatusBarColor = () => {
    if (item.result === CHECK_RESULTS.OK) return "bg-[#3182F6]"; // 토스 블루
    if (item.result === CHECK_RESULTS.FAIL) return "bg-[#F04452]"; // 토스 레드
    if (item.result === CHECK_RESULTS.NA) return "bg-gray-300";
    return "bg-transparent";
  };

  return (
    <div className="relative bg-white">
      {/* 저장 중 오버레이 - 행 전체 블록 */}
      {isSaving && (
        <div className="absolute inset-0 z-10 bg-white/50 flex items-center justify-center">
          <div className="bg-white rounded-full p-2.5 shadow-md">
            <Loader2 className="w-7 h-7 animate-spin text-[#3182F6]" />
          </div>
        </div>
      )}

      {/* 왼쪽 상태 바 */}
      <div
        className={`absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full transition-colors ${getStatusBarColor()}`}
      />

      <div className="pl-4 pr-4 py-3.5 flex items-center gap-3">
        {/* 항목 번호 */}
        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-[11px] font-medium text-gray-500 flex items-center justify-center">
          {index + 1}
        </span>
        {/* 항목명 */}
        <div className="flex-1 min-w-0">
          <p className="text-[14px] leading-snug text-gray-800">{item.item}</p>
        </div>
        <StateButtons
          value={item.result}
          onChange={onResultChange}
          disabled={disabled || isSaving}
        />
      </div>

      {showPhotoSection && item.result === CHECK_RESULTS.OK && (
        <PhotoSectionOk
          item={item}
          onChange={onPhotoChange}
          disabled={disabled || isSaving}
          projectId={projectId}
          docKey={docKey}
          machineId={machineId}
          onCameraOpen={onCameraOpen}
        />
      )}
      {showPhotoSection && item.result === CHECK_RESULTS.FAIL && (
        <PhotoSectionFail
          item={item}
          onChange={onPhotoChange}
          disabled={disabled || isSaving}
          projectId={projectId}
          docKey={docKey}
          machineId={machineId}
          onCameraOpen={onCameraOpen}
        />
      )}

      {/* 구분선 - 마지막 아이템 제외 */}
      {!isLast && <div className="ml-4 mr-4 border-b border-gray-100" />}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function ChecklistTab({
  items,
  onChange,
  onPhotoChange,
  isLoading,
  isSaving,
  savingItemId,
  projectId,
  docKey,
  machineId,
}: ChecklistTabProps) {
  const { groups: groupedItems, isFlat } = useMemo(() => groupCheckItems(items), [items]);

  // 인앱 카메라 상태
  const isNative = Capacitor.isNativePlatform();
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraTarget, setCameraTarget] = useState<{
    itemId: string;
    fieldName: ImageFieldName;
  } | null>(null);

  // 인앱 카메라 열기
  const handleCameraOpen = useCallback((itemId: string, fieldName: ImageFieldName) => {
    setCameraTarget({ itemId, fieldName });
    setCameraOpen(true);
  }, []);

  // 인앱 카메라 촬영 완료
  const handleCameraCapture = useCallback(
    (base64: string) => {
      if (!cameraTarget || !onPhotoChange) return;

      const file = base64ToFile(base64, generatePhotoFileName());
      const targetItem = items.find((item) => item._id === cameraTarget.itemId);
      if (!targetItem || !projectId || !docKey || !machineId) return;

      // 기존 업로드 로직 호출
      uploadCheckItemImage(file, {
        projectId,
        docKey,
        machineId,
        checkId: cameraTarget.itemId,
        fieldName: cameraTarget.fieldName,
      })
        .then((result) => {
          if (result.success && result.url) {
            onPhotoChange({ ...targetItem, [cameraTarget.fieldName]: result.url });
          }
        })
        .catch((error) => {
          logger.error("체크리스트 이미지 업로드 실패:", error);
        });

      // 확인 모드이므로 촬영 후 카메라 닫기
      setCameraOpen(false);
      setCameraTarget(null);
    },
    [cameraTarget, items, onPhotoChange, projectId, docKey, machineId]
  );

  // 인앱 카메라 닫기
  const handleCameraClose = useCallback(() => {
    setCameraOpen(false);
    setCameraTarget(null);
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-col bg-[#F7F8FA] animate-pulse">
        <div className="flex-1 pb-4">
          {[1, 2, 3].map((group) => (
            <div key={group} className="mt-3 first:mt-2">
              <div className="mx-3 bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
                {[1, 2, 3].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-50 last:border-b-0"
                  >
                    <div className="h-4 w-4 rounded bg-slate-200" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 w-16 rounded bg-slate-200" />
                      <div className="h-4 w-32 rounded bg-slate-100" />
                    </div>
                    <div className="h-8 w-16 rounded-lg bg-slate-100" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-6">
        <p className="text-[15px] text-gray-500">점검 항목이 없습니다</p>
      </div>
    );
  }

  const handleResultChange = (item: CheckListItem, result: CheckResultValueNullable) => {
    onChange({ ...item, result });
  };

  return (
    <>
      <div className="flex flex-col bg-[#F7F8FA]">
        <div className="flex-1 pb-4">
          {groupedItems.map((group) => {
            const groupStyle = getGroupStyle(group.group);

            return (
              <div key={group.group || "flat"} className="mt-3 first:mt-2">
                {/* 그룹 헤더 - 플랫 모드(성능점검)에서는 숨김 */}
                {!isFlat && group.group && (
                  <div className="flex items-center gap-2 px-4 py-2.5">
                    <div className={`w-1 h-4 rounded-full ${groupStyle.accent}`} />
                    <span className={`text-[13px] font-semibold ${groupStyle.text}`}>
                      {group.group}
                    </span>
                  </div>
                )}
                {/* 그룹 내 항목들 - 카드 스타일 */}
                <div className="mx-3 bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
                  {group.items.map((item, idx) => (
                    <ChecklistItem
                      key={item._id}
                      item={item}
                      index={idx}
                      isLast={idx === group.items.length - 1}
                      onResultChange={(result) => handleResultChange(item, result)}
                      onPhotoChange={onPhotoChange}
                      disabled={isSaving}
                      isSaving={savingItemId === item._id}
                      projectId={projectId}
                      docKey={docKey}
                      machineId={machineId}
                      onCameraOpen={isNative ? handleCameraOpen : undefined}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 인앱 카메라 (네이티브 환경에서만 사용) */}
      {isNative && (
        <InAppCamera
          isOpen={cameraOpen}
          onCapture={handleCameraCapture}
          onClose={handleCameraClose}
          maxPhotos={1}
        />
      )}
    </>
  );
}

export default ChecklistTab;
